import {
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { getPathWorkLogEdit } from "../../paths";
import { useEngagementQuery } from "../../../../query/engagements/queries/useEngagementQuery";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPayee } from "../../../../query/payee/queries/useQueryPayee";
import { usePayeeEngagementQuery } from "../../../../query/payeeEngagements/queries/usePayeeEngagementQuery";
import { usePayeeRowsQuery } from "../../../../query/search/payee/queries/usePayeeRowsQuery";
import { useQueryWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryWorkDefinitions";
import { useQueryWorkItems } from "../../../../query/workItem/queries/useQueryWorkItems";
import { useQueryWorkLog } from "../../../../query/workLog/queries/useQueryWorkLog";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { usePayableQuery } from "../../../../Invoices/screens/payables/PayableDetails";
import { getPayeeName } from "../../../Payees/selectors/getPayeeNames";
import { RateCardViewWidget } from "../../../RateCards/components/RateCardViewWidget";
import { AlertWorkLogInvoiceStatusPayer } from "../../components/AlertWorkLogInvoiceStatusPayer";
import { CardWorkItems } from "../../components/CardWorkItems";
import { WorkLogAdditionalDetails } from "../../components/WorkLogAdditionalDetails";
import { WorkLogAssignmentInfoPayerView } from "../../components/WorkLogAssignmentInfoPayerView";
import { WorkLogStatusStepperPayerView } from "../../components/WorkLogStatusStepperPayerView";
import { PATH_WORK_LOGS_PAYER } from "../../paths";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";

export const RoutePayerWorkLogDetails: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const workLogId = props.match.params.workLogId;
  const userId = useUserId();
  const history = useHistory();

  // Query work log
  const queryWorkLog = useQueryWorkLog(workLogId);
  const workLog = queryWorkLog.data;

  // Query payable to check if it's accessible
  const queryPayable = usePayableQuery(workLog?.invoiceId!, {
    enabled: !!workLog?.invoiceId,
    retry: false
  });

  // Query payee information
  const queryPayeeRow = usePayeeRowsQuery(
    {
      filter: {
        "engagements.payerPayeeEngagementId": workLog?.payerPayeeEngagementId
      }
    },
    {
      enabled: !!workLog?.payerPayeeEngagementId
    }
  );

  const payeeId = queryPayeeRow.data?.[0]?.payeeId;
  const payerPayeeEngagementId = workLog?.payerPayeeEngagementId;

  const queryPayee = useQueryPayee(payeeId!, {
    enabled: !!payeeId
  });

  const featureFlags = useFeatureFlags();
  const isWorkLogReviewWorkflow = featureFlags.data?.workLogReviewWorkflow;

  const queryPayerPayeeEngagement = usePayeeEngagementQuery(
    payerPayeeEngagementId!,
    {
      enabled: !!payerPayeeEngagementId
    }
  );

  const payee = queryPayee.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  // Query engagement to get work definition IDs
  const engagementId = queryWorkLog.data?.engagementId;

  const queryEngagement = useEngagementQuery(engagementId!, {
    enabled: !!engagementId
  });

  // Extract work definition IDs from engagement
  const workDefinitionIds = useMemo(() => {
    return queryEngagement.data?.workDefinitions?.map(
      wd => wd.workDefinitionId
    );
  }, [queryEngagement.data?.workDefinitions]);

  // Query work definitions using IDs filter
  const queryWorkDefinitions = useQueryWorkDefinitions(
    {
      "filter[workDefinitionId][in]": workDefinitionIds
    },
    {
      enabled: !!workDefinitionIds && workDefinitionIds.length > 0
    }
  );

  // Query work items
  const queryWorkItems = useQueryWorkItems({
    "filter[workLogId][=]": workLogId
  });

  // Browser page title
  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId) {
      return "Work Log Details";
    }

    if (!payee) {
      return "Work Log Details";
    }

    const payeeName = getPayeeName(payee);

    if (payerPayeeEngagement) {
      return `Work Log – ${payeeName} • ${payerPayeeEngagement.engagementName}`;
    }

    return `Work Log – ${payeeName}`;
  }, [payee, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  // Determine if View payable button should be disabled
  const isViewPayableDisabled = useMemo(() => {
    const invoiceId = workLog?.invoiceId;

    if (!invoiceId) {
      return true;
    }

    const isDataEmptyObject =
      !!queryPayable.data && Object.keys(queryPayable.data).length === 0;

    const isPayableNotAccessible =
      (queryPayable.isError &&
        (queryPayable.error?.status === 404 ||
          queryPayable.error?.status === 403)) ||
      isDataEmptyObject;

    return isPayableNotAccessible;
  }, [workLog?.invoiceId, queryPayable]);

  // Action handlers
  const handleViewPayable = useCallback(() => {
    if (workLog?.invoiceId) {
      history.push(`/member/invoices/payables/dashboard/${workLog.invoiceId}`);
    }
  }, [workLog, history]);

  const handleApprove = useCallback(() => {
    // TODO: Implement approve work log mutation
    // This will approve the work log and generate an invoice in Payables
    console.log("Approve work log:", workLogId);
  }, [workLogId]);

  const handleEditWorkLog = useCallback(() => {
    history.push(getPathWorkLogEdit(workLogId));
  }, [history, workLogId]);

  const handleRequestChanges = useCallback(() => {
    // TODO: Implement request changes mutation
    // This will send the work log back to "In-progress" status
    console.log("Request changes for work log:", workLogId);
  }, [workLogId]);

  const handleReject = useCallback(() => {
    // TODO: Implement reject work log mutation
    // This will reject the work log and mark as not invoiced
    console.log("Reject work log:", workLogId);
  }, [workLogId]);

  const workLogActions: WSPageToolbarAction[] = [
    {
      icon: "accept",
      label: "Approve",
      helpText:
        "Approve work log and generate an invoice in your Payables page",
      onClick: handleApprove
    },
    {
      icon: "edit-box",
      label: "Edit work log",
      helpText: "Make changes to the work log yourself",
      onClick: handleEditWorkLog
    },
    {
      icon: "reset",
      label: "Request changes",
      helpText:
        'Send work log back to "In-progress" and ask contractor to make changes',
      onClick: handleRequestChanges
    },
    {
      icon: "block",
      label: "Reject",
      helpText: "Reject the work log and mark as not invoiced",
      destructive: true,
      onClick: handleReject
    }
  ];

  // Error handling - 404 Not Found
  if (queryWorkLog.isError && queryWorkLog.error?.status === 404) {
    return (
      <WSPage>
        <WSEmptyState
          type="search"
          title="Work log not found"
          description="The work log you're looking for doesn't exist or has been deleted."
          actions={{
            buttons: [
              {
                label: "Back to work logs",
                onClick: () => history.push(PATH_WORK_LOGS_PAYER)
              }
            ]
          }}
        />
      </WSPage>
    );
  }

  // Error handling - 403 Forbidden
  if (queryWorkLog.isError && queryWorkLog.error?.status === 403) {
    return (
      <WSPage>
        <WSElement mt="2XL">
          <WSEmptyState
            type="error"
            title="Access denied"
            description="You don't have permission to view this work log."
            actions={{
              buttons: [
                {
                  label: "Back to work logs",
                  onClick: () => history.push(PATH_WORK_LOGS_PAYER)
                }
              ]
            }}
          />
        </WSElement>
      </WSPage>
    );
  }

  // Main content
  return (
    <WSPage>
      <WSPageToolbar
        title={workLog ? `Work log #${workLog.workLogNumber}` : "Work log"}
        breadcrumbs={[
          {
            label: "Work logs",
            onClick: () => history.push(PATH_WORK_LOGS_PAYER)
          },
          {
            label: workLog ? `Work log #${workLog.workLogNumber}` : "Work log"
          }
        ]}
        actions={[
          {
            label: "View payable",
            buttonKind: "Primary",
            onClick: handleViewPayable,
            disabled: isViewPayableDisabled
          },
          ...(isWorkLogReviewWorkflow ? workLogActions : [])
        ]}
      />

      <AlertWorkLogInvoiceStatusPayer workLog={workLog} />

      <WSGrid gutter="XL" mt="L">
        <WSGrid.Item span={{ m: "8" }}>
          <WSList gap="XL">
            <WorkLogAssignmentInfoPayerView
              shimmer={queryPayerPayeeEngagement.isInitialLoading}
              workLog={workLog}
              payee={payee}
              payerPayeeEngagement={payerPayeeEngagement}
            />

            <CardWorkItems
              shimmer={
                queryWorkItems.isInitialLoading ||
                queryEngagement.isInitialLoading ||
                (!!workDefinitionIds &&
                  workDefinitionIds.length > 0 &&
                  queryWorkDefinitions.isInitialLoading)
              }
              noEdit={true}
              workLogId={workLogId}
              payerId={userId!}
              payerPayeeEngagementId={workLog?.payerPayeeEngagementId!}
              workItems={queryWorkItems.data || []}
              workDefinitions={queryWorkDefinitions.data || []}
              resources={{ payerPayeeEngagement }}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ m: "4" }}>
          <WSList gap="XL">
            <WorkLogStatusStepperPayerView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <RateCardViewWidget
              payeeId={payeeId}
              payeeEngagementId={payerPayeeEngagementId}
              context="contractor-assignment"
            />
            <WorkLogAdditionalDetails
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
